import { useQuery } from '@tanstack/react-query';
import type { Contact, Query } from '@/@types/Mesh';
import { useParams } from '@/providers/RouterShim';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import { getGql } from '../../fetch';
import { GET_CUSTOMER_DRAWER_QUERY, GET_CUSTOMER_DRAWER_QUERY_KEY } from './queries';

/**
 * Custom hook to fetch customer data for drawer view
 * Fetches lightweight contact information
 *
 * @param id - Contact ID
 * @returns Tuple of [contact data, request state]
 */
export const useCustomerDrawer = (
  id: string | undefined,
): [Contact | undefined, RequestStateQuery] => {
  const { branchId } = useParams();

  const { data, isPending, isSuccess, isError } = useQuery<Query>({
    queryKey: [GET_CUSTOMER_DRAWER_QUERY_KEY, branchId, id],
    queryFn: async ({ signal }) => {
      if (!id) throw new Error('Customer ID is required');

      return getGql<Query>(
        `${process.env.NEXT_PUBLIC_MESH_URI}/graphql`,
        GET_CUSTOMER_DRAWER_QUERY,
        { id },
        signal,
        branchId,
      );
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false,
  });

  return [
    data?.customer?.contact || undefined,
    {
      isLoading: isPending,
      isSuccess,
      isError,
    },
  ];
};
